import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { commonClassName, commonHeaderClass } from './PromoScorecard';
import { UserContext } from 'contexts/ConfigContext';
import { fetchScopeID, getAdditionalDataOnCopyClick, getConfig, parseAttributeType } from 'utils/helperMethods';
import { CustomSelect } from './GrowthAnalysis';
import DownSvg from 'assets/svg/Down';
import { FilterContextData } from 'contexts/FilterContext';
import _ from 'lodash';
import SelectionDropDown from 'components/molecules/SelectionDropDown';
import { colorConstants } from 'config/colorConstants';
import Legend from 'components/molecules/Legend';
import { getPriceTrackingColumns } from 'utils/getPriceTrackingColumns';
import { CustomTable } from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import styled from '@emotion/styled';
import { Select, Spin, Table } from 'antd';
import tw from 'twin.macro';
import CalenderPriceChange from './CalenderPriceChange';
import dayjs from 'dayjs';
import { useFetchPriceAlert } from 'hooks/priceAlert/useFetchPriceAlertData';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { subHeaderClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { priceTrackingTableDownload } from 'parser/graphToExcelDownloadParser';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import TileButton from 'components/atoms/TileButton';
import useFetchAttributeNameActiveCount from 'hooks/Filter/useFetchAttributeNameActiveCount';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PRICE_TRACKING_COLUMNS_DROPDOWN_CHANGED,
    PRICE_TRACKING_CUSTOM_TIME_PERIOD_CLICKED,
    PRICE_TRACKING_KPI_DROPDOWN_CHANGED,
    PRICE_TRACKING_PRICE_TYPE_DROPDOWN_CHANGED,
    PRICE_TRACKING_ROWS_DROPDOWN_CHANGED,
    PRICE_TRACKING_TIME_PERIOD_DROPDOWN_CHANGED,
    PRICE_TRACKING_WEEKS_DROPDOWN_CHANGED,
} from 'utils/mixpanelEventNames';
import { ConfigKeys } from 'utils/Enums';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { characterLimit } from 'components/molecules/TradePromoOptimization/Modals/CreateEditPlanContainer';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import { MaxTagComponent } from 'components/molecules/PricingSimulator/Modals/CreateEditProjectContainer';
import { priceTrackingFrequencyMapping } from 'config/constants';

const priceTrackingMainHeader = 'Price Tracking';

const height = 700;

const priceTrackerScrollerId = 'price-alert-infinite-scroller';
const { Option } = Select;

export const CustomTablePriceTracking = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
        display: none;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 68px;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 50px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }

    border: ${'1px solid #DBDFE9'};
`;

const legendData = [
    { label: 'Positive', color: colorConstants.limeGreen, style: { height: 8, width: 8 } },
    { label: 'Neutral', color: colorConstants.orangeAlt, style: { height: 8, width: 8 } },
    { label: 'Negative', color: colorConstants.red, style: { height: 8, width: 8 } },
];

const PriceTracking = () => {
    const { appConfig } = useContext(UserContext);
    const priceTrackConfig = getConfig('priceTracking', appConfig);
    const filterConfig = getConfig('globalFilter', appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const {
        allDimAndAttributeTypeList,
        currentFrequency,
        idForRestOfComponent,
        timePeriodSelection,
        setHideGlobalTimeFilterComponent,
    } = useContext(FilterContextData);

    const {
        dimensionConfig: {
            //snake case eg sub_category
            excludedDimensions,
            defaultRowDimension,
            defaultColumnDimension,
        },
        timePeriodOptions,
        weeksOptions,
        kpiOptions,
        priceTypeOptions,
    } = priceTrackConfig;

    const frequencyOptions =
        priceTrackConfig[priceTrackingFrequencyMapping[currentFrequency?.key || 'W']] || weeksOptions;

    const [firstStartRange, setFirstStartRange] = useState('');
    const [secondStartRange, setSecondStartRange] = useState('');
    const [subTileState, setSubTileState] = useState(priceTrackConfig.subTileData[0]);

    const [firstEndRange, setFirstEndRange] = useState('');
    const [secondEndRange, setSecondEndRange] = useState('');

    const [selectedTimePeriodDropdown, setSelectedTimePeriodDropdown] = useState<any>(timePeriodOptions?.[0]);
    const [selectedRowsDropdown, setSelectedRowsDropdown] = useState<any>(null);
    const [selectedColumnsDropdown, setSelectedColumnsDropdown] = useState<any>(null);
    const [selectedWeeksDropdown, setSelectedWeeksDropdown] = useState<any[]>([frequencyOptions?.[0]?.value]);
    const [selectedPriceTypeDropdown, setSelectedPriceTypeDropdown] = useState<any>(priceTypeOptions?.[0]);
    const [selectedKpiDropdown, setSelectedKpiDropdown] = useState<any>(kpiOptions?.[0]);
    const [selectedRowAttributeNameDropdown, setSelectedRowAttributeNameDropdown] = useState<any>(null);
    const [selectedColumnAttributeNameDropdown, setSelectedColumnAttributeNameDropdown] = useState<any>(null);
    const [rowsOptions, setRowsOptions] = useState<any[]>([]);
    const [columnsOptions, setColumnsOptions] = useState<any[]>([]);
    const [expandedIndex, setExpandedIndex] = useState<any>(null);
    const tableOffsetRef = useRef<number>(0);
    const rowOffsetRef = useRef<number>(0);
    const columnOffsetRef = useRef<number>(0);
    const tableCopyRef = useRef<any>();
    const [rowSearchValue, setRowSearchValue] = useState<string>('');
    const [columnSearchValue, setColumnSearchValue] = useState<string>('');
    const rowElemRef = useRef<any>();
    const columnElemRef = useRef<any>();

    const {
        data: priceAlertData,
        error: priceAlertError,
        loading: priceAlertLoading,
        fetchPriceAlertData,
        count: priceAlertCount,
        emptyData: emptyPriceAlertData,
    } = useFetchPriceAlert();

    const {
        data: rowAttributeNamesData,
        error: rowAttributeNamesError,
        loading: rowAttributeNamesLoading,
        count: rowAttributeNamesCount,
        fetchAttributeNamesAlt: fetchRowAttributeNames,
    } = useFetchAttributeNameActiveCount();
    const {
        data: columnAttributeNamesData,
        error: columnAttributeNamesError,
        loading: columnAttributeNamesLoading,
        count: columnAttributeNamesCount,
        fetchAttributeNamesAlt: fetchColumnAttributeNames,
    } = useFetchAttributeNameActiveCount();

    // for price tracking we want to hide time period filter selection in the global filter
    useEffect(() => {
        setHideGlobalTimeFilterComponent(true);
        // resetting the state
        return () => {
            setHideGlobalTimeFilterComponent(false);
        };
    }, []);

    useEffect(() => {
        setSelectedWeeksDropdown([frequencyOptions?.[0]?.value]);
    }, [currentFrequency?.key]);

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            let data = parseAttributeType(allDimAndAttributeTypeList['dimentions'], excludedDimensions);
            if (data.length) {
                setColumnsOptions(data);
                setRowsOptions(data);

                setSelectedRowsDropdown(data.find((item: any) => item.label === defaultRowDimension) || data[0]);
                setSelectedColumnsDropdown(data.find((item: any) => item.label === defaultColumnDimension) || data[1]);
                // setAttributeTypesList(data);
                // setSelectPPG(data?.[0]);
            }
        }
    }, [allDimAndAttributeTypeList?.loading]);
    const filteredRowsColumns = useMemo(() => {
        const rows = rowsOptions.filter((item) => item?.label !== selectedColumnsDropdown?.label) || [];
        const columns = columnsOptions.filter((item) => item?.label !== selectedRowsDropdown?.label) || [];

        return {
            rows,
            columns,
        };
    }, [selectedRowsDropdown, selectedColumnsDropdown]);

    const weeksSelectionWithLabels = useMemo(
        () => frequencyOptions.filter((item: any) => selectedWeeksDropdown.includes(item.value)),
        [selectedWeeksDropdown],
    );

    const memoisedColumns = useMemo(() => {
        return getPriceTrackingColumns(
            priceAlertData?.tableData,
            weeksSelectionWithLabels,
            expandedIndex,
            setExpandedIndex,
            selectedRowsDropdown?.label,
            selectedTimePeriodDropdown?.value,
            selectedColumnAttributeNameDropdown?.label,
            selectedRowAttributeNameDropdown?.label,
            decimalForNonPercentage,
            decimalForPercentage,
        );
    }, [priceAlertData?.tableData, expandedIndex]);

    // useEffect(() => {
    //     if (timePeriodSelection && !firstEndRange && !firstStartRange) {
    //         // TODO: temporarily we will be using global time period
    //         //updateCustomDateRange();
    //     }
    // }, [timePeriodSelection]);

    useEffect(() => {
        if (selectedRowsDropdown?.label && selectedTimePeriodDropdown?.value !== 'custom') {
            tableOffsetRef.current = 0;
            setExpandedIndex(null);
            fetchPriceAlertDataWrapper(tableOffsetRef.current);
        }
    }, [
        subTileState,
        selectedRowsDropdown,
        selectedRowAttributeNameDropdown,
        selectedColumnAttributeNameDropdown,
        selectedPriceTypeDropdown,
        selectedKpiDropdown,
        selectedColumnsDropdown,
        selectedWeeksDropdown,
        selectedTimePeriodDropdown,
        idForRestOfComponent,
    ]);

    useEffect(() => {
        if (selectedTimePeriodDropdown?.value === 'custom') {
            emptyPriceAlertData();
            tableOffsetRef.current = 0;
        }
        if (
            selectedTimePeriodDropdown?.value === 'custom' &&
            firstStartRange &&
            firstEndRange &&
            secondEndRange &&
            secondStartRange
        ) {
            tableOffsetRef.current = 0;
            const infScrollerElem = document
                .getElementById(priceTrackerScrollerId)
                ?.getElementsByClassName('infinite-scroll-component')[0];
            if (infScrollerElem) infScrollerElem.scrollTo(0, 0);

            setExpandedIndex(null);
            fetchPriceAlertDataWrapper(tableOffsetRef.current);
        }
    }, [
        subTileState,
        selectedTimePeriodDropdown,
        firstStartRange,
        firstEndRange,
        secondStartRange,
        secondEndRange,
        selectedPriceTypeDropdown,
        selectedKpiDropdown,
        selectedRowAttributeNameDropdown,
        selectedColumnAttributeNameDropdown,

        selectedRowsDropdown,
        selectedColumnsDropdown,
        idForRestOfComponent,
    ]);

    const commonVariables = useMemo(() => {
        return {
            limit: priceTrackConfig?.attributeNamesLimit,
            searchKeyword: '',
            filters: JSON.stringify(idForRestOfComponent),
            rbacFilters: fetchScopeID(filterConfig.defaultIdObject),
        };
    }, [idForRestOfComponent]);

    useEffect(() => {
        if (selectedKpiDropdown && selectedKpiDropdown?.value === 'index') {
            rowOffsetRef.current = 0;
            columnOffsetRef.current = 0;
            fetchRowAttributeNamesWrapper();
            fetchColumnAttributeNamesWrapper();
        }
    }, [selectedKpiDropdown, selectedColumnsDropdown, selectedRowsDropdown]);

    useEffect(() => {
        if (selectedKpiDropdown && selectedKpiDropdown?.value === 'index') {
            setSelectedColumnAttributeNameDropdown(null);
            setSelectedRowAttributeNameDropdown(null);
            rowOffsetRef.current = 0;
            columnOffsetRef.current = 0;
            fetchRowAttributeNamesWrapper();
            fetchColumnAttributeNamesWrapper();
        }
    }, [idForRestOfComponent]);

    function fetchRowAttributeNamesWrapper(offset: number = 0, searchKey: string = '') {
        fetchRowAttributeNames({
            variables: {
                ...commonVariables,
                attributeType: _.snakeCase(selectedRowsDropdown?.label),
                offset: offset || rowOffsetRef.current,
                dimension: selectedRowsDropdown?.dimension,
                isAttribute: Boolean(selectedRowsDropdown?.isAttribute),
                searchKeyword: searchKey,
            },
        });
    }
    function fetchColumnAttributeNamesWrapper(offset: number = 0, searchKey: string = '') {
        fetchColumnAttributeNames({
            variables: {
                ...commonVariables,
                attributeType: _.snakeCase(selectedColumnsDropdown?.label),
                dimension: selectedColumnsDropdown?.dimension,
                offset: offset || columnOffsetRef.current,
                isAttribute: Boolean(selectedColumnsDropdown?.isAttribute),
                searchKeyword: searchKey,
            },
        });
    }

    const memoisedAttributeNames = useMemo(() => {
        let rowData: any[] = [];
        let columnData: any[] = [];
        if (rowAttributeNamesData?.length) {
            rowData = rowAttributeNamesData
                ?.filter((item: any) => item?.isActive)
                ?.map((item: any) => {
                    return {
                        label: item?.attributeExId,
                        value: item?.attributeExId,
                    };
                });
        }
        if (columnAttributeNamesData?.length) {
            columnData = columnAttributeNamesData
                ?.filter((item: any) => item?.isActive)
                ?.map((item: any) => {
                    return {
                        label: item?.attributeExId,
                        value: item?.attributeExId,
                    };
                });
        }
        return { rowData, columnData };
    }, [rowAttributeNamesData, columnAttributeNamesData]);

    function fetchPriceAlertDataWrapper(offset: number) {
        const startDate = {
            start_timestamp: dayjs(firstStartRange, appConfig?.commonConfig?.dateFormat).format('MM/DD/YYYY'),
            end_timestamp: dayjs(firstEndRange, appConfig?.commonConfig?.dateFormat).format('MM/DD/YYYY'),
        };
        const endDate = {
            start_timestamp: dayjs(secondStartRange, appConfig?.commonConfig?.dateFormat).format('MM/DD/YYYY'),
            end_timestamp: dayjs(secondEndRange, appConfig?.commonConfig?.dateFormat).format('MM/DD/YYYY'),
        };
        fetchPriceAlertData({
            variables: {
                comparisonPeriodType: selectedTimePeriodDropdown?.value,
                periodsList: selectedTimePeriodDropdown?.value === 'custom' ? [] : selectedWeeksDropdown,
                rowDimension: selectedRowsDropdown?.dimension,
                rowAttributeType: _.snakeCase(selectedRowsDropdown?.label),
                filters: JSON.stringify(idForRestOfComponent),
                columnDimension: selectedColumnsDropdown?.dimension,
                columnAttributeType: _.snakeCase(selectedColumnsDropdown?.label),
                limit: priceTrackConfig?.limit,
                offset: offset,
                frequency: currentFrequency?.key,
                metricType: subTileState?.key,
                dateRange: [JSON.stringify(startDate), JSON.stringify(endDate)],
                priceType: selectedPriceTypeDropdown?.value,
                kpiName: selectedKpiDropdown?.value,
                selectedRow: selectedRowAttributeNameDropdown?.value || '',
                selectedColumn: selectedColumnAttributeNameDropdown?.value || '',
            },
        });
    }

    function updateCustomDateRange() {
        const { start_timestamp, end_timestamp } = JSON.parse(timePeriodSelection);
        //rounding of to sunday
        setFirstStartRange(dayjs.unix(start_timestamp).startOf('week').format(appConfig?.commonConfig?.dateFormat));
        setFirstEndRange(dayjs.unix(end_timestamp).startOf('week').format(appConfig?.commonConfig?.dateFormat));

        // year ago dates
        setSecondStartRange(
            dayjs.unix(start_timestamp).subtract(1, 'year').startOf('week').format(appConfig?.commonConfig?.dateFormat),
        );
        setSecondEndRange(
            dayjs.unix(end_timestamp).subtract(1, 'year').startOf('week').format(appConfig?.commonConfig?.dateFormat),
        );
    }

    function infiniteScrollOnNext(loading: boolean) {
        if (loading) return;
        tableOffsetRef.current = tableOffsetRef.current + priceTrackConfig.limit;
        fetchPriceAlertDataWrapper(tableOffsetRef.current);
    }
    const trackerExcelData = useMemo(() => {
        return priceTrackingTableDownload(
            priceAlertData?.tableData,
            weeksSelectionWithLabels,
            selectedRowsDropdown,
            selectedTimePeriodDropdown,
        );
    }, [priceAlertData?.tableData]);
    //infinite scroller function
    const dropdownInfiniteScrollOnNext = (from: 'row' | 'column', loading: boolean) => {
        if (!loading) {
            if (from === 'row') {
                rowOffsetRef.current = rowOffsetRef.current + priceTrackConfig?.attributeNamesLimit;
                fetchRowAttributeNamesWrapper();
            } else {
                columnOffsetRef.current = columnOffsetRef.current + priceTrackConfig?.attributeNamesLimit;
                fetchColumnAttributeNamesWrapper();
            }
        }
    };
    //debounced infinite scroller function for dropdown
    const debouncedInfiniteScroller = useCallback(_.debounce(dropdownInfiniteScrollOnNext, 150), [
        commonVariables,
        selectedRowsDropdown,
        selectedColumnsDropdown,
    ]);

    function handleSearch(val: string, from: 'row' | 'column') {
        if (from === 'row') {
            fetchRowAttributeNamesWrapper(undefined, val);
        } else {
            fetchColumnAttributeNamesWrapper(undefined, val);
        }
    }

    //debounced search handler
    const debouncedHandleSearch = useCallback(_.debounce(handleSearch, 400), [
        commonVariables,
        selectedRowsDropdown,
        selectedColumnsDropdown,
    ]);

    const infiniteScrollOnNextMemoisedRows = useCallback(() => {
        if (rowAttributeNamesLoading) return;
        const newOffset = rowOffsetRef.current + priceTrackConfig?.attributeNamesLimit;
        rowOffsetRef.current = newOffset;
        fetchRowAttributeNamesWrapper(newOffset, rowSearchValue);
    }, [rowOffsetRef.current, rowAttributeNamesLoading]);

    const infiniteScrollOnNextMemoisedColumns = useCallback(() => {
        if (columnAttributeNamesLoading) return;
        const newOffset = columnOffsetRef.current + priceTrackConfig?.attributeNamesLimit;
        columnOffsetRef.current = newOffset;
        fetchColumnAttributeNamesWrapper(newOffset, columnSearchValue);
    }, [columnOffsetRef.current, columnAttributeNamesLoading]);

    return (
        <section ref={tableCopyRef} className={`${commonClassName} bg-white`}>
            <h2 className={commonHeaderClass}>{priceTrackingMainHeader}</h2>
            <div className="flex justify-between my-4">
                <div className="flex items-center gap-4">
                    <div>
                        <p className="font-xs pb-1">Time Period</p>
                        <CustomSelect
                            className="dropdown-height dropdown-width-multi-select uniqSelPTTimePeriod"
                            suffixIcon={<DownSvg />}
                            value={selectedTimePeriodDropdown?.value}
                            disabled={false}
                            options={timePeriodOptions}
                            onChange={(val: any, item: any) => {
                                setSelectedTimePeriodDropdown(item);

                                //mixpanel event
                                trackEventMixpanel(PRICE_TRACKING_TIME_PERIOD_DROPDOWN_CHANGED, {
                                    timePeriodName: item?.label,
                                });
                            }}
                        />
                    </div>
                    {selectedTimePeriodDropdown?.value === 'custom' && (
                        <div className="flex gap-4 ">
                            <div
                                onClick={() => {
                                    //mixpanel event
                                    trackEventMixpanel(PRICE_TRACKING_CUSTOM_TIME_PERIOD_CLICKED, {
                                        from: 'Current Time Period',
                                    });
                                }}
                                className="w-[180px]"
                            >
                                <CalenderPriceChange
                                    position="bottom"
                                    setStartDate={setFirstStartRange}
                                    setEndDate={setFirstEndRange}
                                    startDate={firstStartRange}
                                    endDate={firstEndRange}
                                    calenderHeader="Current Time Period"
                                />
                            </div>

                            <div className="pt-7">vs</div>
                            <div
                                onClick={() => {
                                    //mixpanel event
                                    trackEventMixpanel(PRICE_TRACKING_CUSTOM_TIME_PERIOD_CLICKED, {
                                        from: 'Base Line Period',
                                    });
                                }}
                                className="w-[180px]"
                            >
                                <CalenderPriceChange
                                    position="bottom"
                                    setStartDate={setSecondStartRange}
                                    setEndDate={setSecondEndRange}
                                    startDate={secondStartRange}
                                    endDate={secondEndRange}
                                    calenderHeader="Base Line Period"
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex ml-5 items-end justify-end gap-4">
                    <div>
                        <p className="font-xs pb-1">Rows</p>
                        <CustomSelect
                            className="dropdown-height dropdown-width-multi-select uniqSelPTRows"
                            suffixIcon={<DownSvg />}
                            value={selectedRowsDropdown?.value}
                            disabled={allDimAndAttributeTypeList?.loading}
                            options={filteredRowsColumns.rows}
                            onChange={(val: any, item: any) => {
                                setSelectedRowAttributeNameDropdown(null);
                                setSelectedRowsDropdown(item);
                                //mixpanel event
                                trackEventMixpanel(PRICE_TRACKING_ROWS_DROPDOWN_CHANGED, {
                                    rowName: item?.label,
                                });
                            }}
                        />
                    </div>
                    <div>
                        <p className="font-xs pb-1">Columns</p>
                        <CustomSelect
                            className="dropdown-height dropdown-width-multi-select uniqSelPTColumns"
                            suffixIcon={<DownSvg />}
                            value={selectedColumnsDropdown?.value}
                            disabled={allDimAndAttributeTypeList?.loading}
                            options={filteredRowsColumns.columns}
                            onChange={(val: any, item: any) => {
                                setSelectedColumnAttributeNameDropdown(null);
                                setSelectedColumnsDropdown(item);
                                //mixpanel event
                                trackEventMixpanel(PRICE_TRACKING_COLUMNS_DROPDOWN_CHANGED, {
                                    columnName: item?.label,
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-between my-4">
                <div className="flex items-center gap-4">
                    <div>
                        <p className="font-xs pb-1">{currentFrequency?.label ?? 'Week'}</p>

                        <SelectionDropDown
                            className="dropdown-width-multi-select uniqSelPTWeeks"
                            list={frequencyOptions}
                            checkedListObject={''}
                            disabled={selectedTimePeriodDropdown?.value === 'custom'}
                            setSelectedList={setSelectedWeeksDropdown}
                            selectedList={selectedWeeksDropdown}
                            eventMixPanelString={PRICE_TRACKING_WEEKS_DROPDOWN_CHANGED}
                        />
                    </div>
                    <div>
                        <p className="font-xs pb-1">Price Type</p>
                        <CustomSelect
                            className="dropdown-height dropdown-width-multi-select uniqSelPTPriceType"
                            suffixIcon={<DownSvg />}
                            value={selectedPriceTypeDropdown?.value}
                            options={priceTypeOptions}
                            onChange={(val: any, item: any) => {
                                setSelectedPriceTypeDropdown(item);
                                //mixpanel event
                                trackEventMixpanel(PRICE_TRACKING_PRICE_TYPE_DROPDOWN_CHANGED, {
                                    priceTypeName: item?.label,
                                });
                            }}
                        />
                    </div>
                    <div>
                        <p className="font-xs pb-1">KPI</p>
                        <CustomSelect
                            className="dropdown-height dropdown-width-multi-select uniqSelPTKPI"
                            suffixIcon={<DownSvg />}
                            value={selectedKpiDropdown?.value}
                            options={kpiOptions}
                            onChange={(val: any, item: any) => {
                                setSelectedKpiDropdown(item);
                                //mixpanel event
                                trackEventMixpanel(PRICE_TRACKING_KPI_DROPDOWN_CHANGED, {
                                    kpiName: item?.label,
                                });
                            }}
                        />
                    </div>
                </div>
                {selectedKpiDropdown?.value === 'index' && (
                    <div className="flex items-center gap-4">
                        <div>
                            <p className="font-xs pb-1">Select {selectedRowsDropdown?.label}</p>
                            <SelectWithInfiniteScroller
                                hasMore={memoisedAttributeNames?.rowData?.length < rowAttributeNamesCount}
                                onInfiniteScrollOnNextMemoised={infiniteScrollOnNextMemoisedRows}
                                loading={rowAttributeNamesLoading}
                                style={{ width: '180px' }}
                                mode={undefined}
                                placeholder={`Select ${selectedRowsDropdown?.label}`}
                                maxTagCount={2}
                                maxTagPlaceholder={(val) => {
                                    const remainingCount = val.length || 0;
                                    return <MaxTagComponent remainingCount={remainingCount} />;
                                }}
                                filterOption={false}
                                showArrow
                                autoClearSearchValue={false}
                                searchValue={rowSearchValue}
                                showSearch
                                dropdownRender={(menu) => menu}
                                onSearch={(val) => {
                                    setRowSearchValue(val);
                                    debouncedHandleSearch(val, 'row');
                                }}
                                notFoundContent={
                                    rowAttributeNamesLoading ? (
                                        <div className="p-3">
                                            <Loader />{' '}
                                        </div>
                                    ) : (
                                        <ErrorBox msg="No Data" />
                                    )
                                }
                                maxTagTextLength={characterLimit}
                                value={selectedRowAttributeNameDropdown ? selectedRowAttributeNameDropdown.value : null}
                                onChange={(val: any, item) => {
                                    setSelectedRowAttributeNameDropdown(item);
                                }}
                                options={memoisedAttributeNames?.rowData}
                            />
                        </div>
                        <div>
                            <p className="font-xs pb-1">Select {selectedColumnsDropdown?.label}</p>
                            <SelectWithInfiniteScroller
                                hasMore={memoisedAttributeNames?.columnData?.length < columnAttributeNamesCount}
                                onInfiniteScrollOnNextMemoised={infiniteScrollOnNextMemoisedColumns}
                                loading={columnAttributeNamesLoading}
                                style={{ width: '180px' }}
                                mode={undefined}
                                placeholder={`Select ${selectedColumnsDropdown?.label}`}
                                maxTagCount={2}
                                maxTagPlaceholder={(val) => {
                                    const remainingCount = val.length || 0;
                                    return <MaxTagComponent remainingCount={remainingCount} />;
                                }}
                                filterOption={false}
                                showArrow
                                autoClearSearchValue={false}
                                searchValue={columnSearchValue}
                                showSearch
                                dropdownRender={(menu) => menu}
                                onSearch={(val) => {
                                    setColumnSearchValue(val);
                                    debouncedHandleSearch(val, 'column');
                                }}
                                notFoundContent={
                                    columnAttributeNamesLoading ? (
                                        <div className="p-3">
                                            <Loader />{' '}
                                        </div>
                                    ) : (
                                        <ErrorBox msg="No Data" />
                                    )
                                }
                                maxTagTextLength={characterLimit}
                                value={
                                    selectedColumnAttributeNameDropdown
                                        ? selectedColumnAttributeNameDropdown.value
                                        : null
                                }
                                onChange={(val: any, item) => {
                                    setSelectedColumnAttributeNameDropdown(item);
                                }}
                                options={memoisedAttributeNames?.columnData}
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className="mb-2">
                <h2 className={`${subHeaderClass}`}>
                    {selectedPriceTypeDropdown?.label} {subTileState?.tooltip} {selectedKpiDropdown?.label} Table
                </h2>
            </div>
            <div className="flex mb-4 items-center justify-between">
                <Legend data={legendData} />
                <div className="flex gap-4 items-center">
                    {!(allDimAndAttributeTypeList?.loading || priceAlertLoading) && (
                        <div className="flex items-center gap-2">
                            <CopyToClipBoard refOfComponent={tableCopyRef} />
                            <DownloadExcel
                                title={'PriceTrackerTable'}
                                headers={trackerExcelData.headers}
                                excelData={trackerExcelData.data}
                            />
                        </div>
                    )}
                    <TileButton
                        disabled={allDimAndAttributeTypeList?.loading}
                        onClick={(item: any) => {
                            setSubTileState(item);
                        }}
                        activeKey={subTileState.key}
                        data={priceTrackConfig.subTileData}
                    />
                </div>
            </div>
            <div id={priceTrackerScrollerId}>
                <InfiniteScroller
                    data={priceAlertData?.tableData || []}
                    loading={allDimAndAttributeTypeList?.loading || priceAlertLoading}
                    hasMore={
                        tableOffsetRef.current + priceTrackConfig.limit < priceAlertCount ||
                        allDimAndAttributeTypeList?.loading ||
                        priceAlertLoading
                    }
                    height={priceAlertData?.tableData && !priceAlertError ? height : 'auto'}
                    //height={200}
                    //     loader={true}
                    onNext={() => infiniteScrollOnNext(priceAlertLoading)}
                >
                    <CustomTablePriceTracking
                        bordered
                        style={{
                            opacity: allDimAndAttributeTypeList?.loading || priceAlertLoading ? '0.3' : 1,
                            pointerEvents: allDimAndAttributeTypeList?.loading || priceAlertLoading ? 'none' : 'auto',
                        }}
                        columns={memoisedColumns}
                        pagination={false}
                        scroll={{ x: '100%' }}
                        sticky
                        // loading={allDimAndAttributeTypeList?.loading || priceAlertLoading}
                        dataSource={priceAlertData?.tableData}
                    />
                </InfiniteScroller>
            </div>
        </section>
    );
};

export default PriceTracking;
